import PropTypes from 'prop-types';
import React     from 'react';

import * as S                   from './switch-to-simp.module.scss';
import { ModalWrapper }         from '../../modal-wrapper';
import { PlayBtn }              from '../../../common/assets/play-btn';
import LOGO_SIMPLIFI            from '../../../../assets/imgs/brand/rebrand-qkn-blurple-logo.svg';
import CIRCLE_CHECK             from '../../../../assets/imgs/icons/circle-check-blurple.svg';
import SIMP_SCREENSHOT          from '../../../../assets/imgs/brand/switch-simp-img.webp';
import { mergeMainStateObj }    from '../../../../context/ctx-actions';
import { useGlobalCtx }         from '../../../../context/ctx-hook';
import * as ProductSelectors    from '../../../../context/selectors/product-selectors';
import { MODALS }               from '../../../../helpers/constants/state-constants';
import { checkRebrandName }     from '../../../../helpers/pure-functions';
import { 
  getFullDate, 
  monthsLeft 
}                               from '../../../../helpers/time-helpers';
import { callAddSubscription }  from '../../../../network/network-calls';
import { gtmLink } from '../../../../helpers/tealium';

const POINTS = [
  'Always know what you have left to spend or save',
  'Get better insights with reports & real-time alerts',
  'Make informed decisions about your investment portfolio',
  'Plan for the future with projected cash flows'
];

const renderTracking = async () => {
  gtmLink({
    event: 'trackEvent',
    eventCategory: 'My Account',
    eventAction: 'Viewed - Try Simplifi renewal offer',
    trySimplifiFree: 1
  });
};

const trackSwitchSubmit = async () => {
  gtmLink({
    'event': 'transaction',
    'eventCategory': 'ecommerce',
    'eventAction': 'transaction',
    'eventLabel': 'Try Simplifi for Free - Offer',
    'productQuantity': 1,
    'productSKU': '170415',

    //Keys we need to check if data exists for:
    'orderID': 'orderID',
    'productEdition': 'productName',
    'productYear': 'productYear',
    'productPlatform': 'platform',
    'purchasePrice': 'price',
  });
};


const SwitchToSimp = ({onClose, ctx: {product}}) => {
  const {state, globalDispatch} = useGlobalCtx();

  const {expireOn} = ProductSelectors.getSubscription(state, product);
  const NAME       = ProductSelectors.getProductName(state, product);
  const DATE       = getFullDate(expireOn);
  const MONTHS_LEFT = monthsLeft(expireOn);
  const IS_LAST_MONTH = MONTHS_LEFT <= 1;
  // HARDCODED SKUS WOOOOOO
  const SWITCH_TO_SIMP_SKU = '170525';
  const SWITCH_SKU_PRICE = state?.skus[SWITCH_TO_SIMP_SKU]?.RenewalPrice || '';

  const [showVideo, setShowVideo] = React.useState(false);

  const MESSAGE = `Your renewal has been cancelled. You’ll be able to use ${checkRebrandName(NAME)} until ${DATE}.`;

  const handleSwitchDecline = () => {
    globalDispatch(mergeMainStateObj({
      modal: {
        type: MODALS.SURVEY,
        surveySrc: 'SWITCH_SIMP_CANCEL_SURVEY',
        ctx: { product },
        isLoading: true,
        hasError: false,
        isDisabled: false,
      }
    }));
  };

  const handleSubmit = React.useCallback(async () => {
    globalDispatch(mergeMainStateObj({
      isDisabled: true,
      modal:      {isLoading: true, isDisabled: true, hasError: false}
    }));

    try {
      const {data} = await callAddSubscription(
        {
          renewalFrequency:  'P1Y',
          sku:              SWITCH_TO_SIMP_SKU,
          spec:             {
            productLineUriName: 'acme',
            tierUriName:        'acme-basic',
            expirationDate:     expireOn,
            term:               'P1Y',
            isTrial:            true
          }
        });

      trackSwitchSubmit();

      globalDispatch(mergeMainStateObj({
        isDisabled:    false,
        subscriptions: ProductSelectors.mergeSubscription(state, 'acme', data.postState),

        modal: {
          type:       MODALS.SWITCH_SUCCESS,
          ctx:        {name: NAME, date: DATE},
          isLoading:  false,
          hasError:   false,
          isDisabled: false
        }
      }));

    } catch (err) {
      globalDispatch(mergeMainStateObj({
        isDisabled: false,
        modal:      {isLoading: false, isDisabled: false, hasError: true}
      }));
    }
  }, []);

  { renderTracking();}

  return (
    <ModalWrapper
      hideClose
      onClose={onClose}
      className={S.modalWrapper}
      heading="Renewal Cancellation Success"
    >
      <div className={S.modalArea}>
        <div className={S.upperMessage}>
          <p>{MESSAGE}</p>
        </div>

        <div className={S.shrinkingAreaContainer}>
          <div className={S.shrinkingArea}>
            <div className={S.simpBranding}>
              <img src={LOGO_SIMPLIFI} alt="Simplifi by Quicken"/>
              <div className={S.brandText}>
                <h4>A free gift before you go!</h4> 
                <p>Enjoy our personal finance app, Quicken Simplifi,
                  { IS_LAST_MONTH && ' free for the next month'}
                  { !IS_LAST_MONTH && ' for the rest of your subscription.'}
                </p>
              </div>
            </div>
            <div className={S.middleRow}>
              <div className={S.videoArea}>
                {!showVideo && (
                  <>
                    {/* eslint-disable-next-line */}
                    <a 
                      onClick={() => setShowVideo(true)}
                    >
                      <img
                        src={SIMP_SCREENSHOT}
                        className={S.preview}
                        alt="Screenshot of Simplifi home screen"
                      />
                      <div className={S.svgCont}>
                        <PlayBtn/>
                      </div>
                    </a>
                  </>
                )}

                {showVideo && (
                  <iframe
                    className={S.iframe}
                    title="Meet Simplifi"
                    src="https://www.youtube.com/embed/3TuPlHL4A-M?enablejsapi=1&amp;autoplay=1&rel=0&controls=0"
                    frameBorder="0"
                    allow="autoplay"
                  />
                )}
              </div>

              <div className={S.textArea}>
                <ul>
                  {POINTS.map((point, i) => (
                    <li key={i}>
                      <img src={CIRCLE_CHECK} alt="CheckMark"/>
                      <p>{point}</p>
                    </li>
                  ))}
                </ul>

                <div className={S.btnContain}>
                  <p className={S.switchLegal}>
                    <b>Automatic Renewal Authorization:</b> By clicking  &apos;Try Simplifi &apos;, you authorize Quicken to automatically renew your membership each year at the then-current subscription fee (currently {SWITCH_SKU_PRICE}) at the end of your term. You can turn off automatic renewal anytime in &apos;My Account&apos; or by contacting customer support. By subscribing, you agree to the <a href="https://quicken.com/terms">Quicken Terms of Use</a> and <a href="https://quicken.com/privacy">Privacy Policy</a>.
                  </p>
                  <div className={S.btnRow}>
                    <button
                      type="button"
                      onClick={handleSwitchDecline}
                      className={`${S.outlineCTABtn} decline`}
                      disabled={state.modal.isDisabled}
                    >No thanks
                    </button>

                    <button
                      type="button"
                      className={`${S.contentCTABtn} accept`}
                      onClick={handleSubmit}
                      disabled={state.modal.isDisabled}
                    >Try Simplifi </button>
                  </div>
                  {state.modal.hasError &&
                    <p className={S.error}>Server Error occurred please try again</p>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

SwitchToSimp.displayName = 'SwitchToSimp';
SwitchToSimp.propTypes   = {
  onClose: PropTypes.func.isRequired,
  ctx:     PropTypes.shape({
    product: PropTypes.string.isRequired
  })
};

export { SwitchToSimp };
